/**
 * BadgeDetailModal — Modal for badge details on click (Phase 5 P0)
 *
 * Shows enlarged badge icon, name, description, earned date, and criteria.
 * For unearned badges, shows progress toward earning.
 *
 * Props:
 *   open     — boolean controlling modal visibility
 *   onClose  — callback to close the modal
 *   badge    — badge object { badge_key, name, description, icon_url, earned, earned_at, criteria, progress }
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Lock,
  EmojiEvents,
  Share,
} from '@mui/icons-material';

// Map badge_key → emoji fallback
const BADGE_ICONS = {
  first_interview: '🎤',
  five_interviews: '🏆',
  perfect_score: '💯',
  seven_day_streak: '🔥',
  thirty_day_streak: '🌟',
  resume_uploaded: '📄',
  speed_demon: '⚡',
  community_connector: '🤝',
  ten_interviews: '🎯',
  fifty_interviews: '👑',
  hundred_interviews: '💎',
};

// Map badge_key → criteria descriptions for unearned badges
const BADGE_CRITERIA = {
  first_interview: 'Complete your first mock interview',
  five_interviews: 'Complete 5 mock interviews',
  ten_interviews: 'Complete 10 mock interviews',
  fifty_interviews: 'Complete 50 mock interviews',
  hundred_interviews: 'Complete 100 mock interviews',
  perfect_score: 'Achieve a perfect 10/10 score in an interview',
  seven_day_streak: 'Practice for 7 consecutive days',
  thirty_day_streak: 'Practice for 30 consecutive days',
  resume_uploaded: 'Upload your first resume',
  speed_demon: 'Complete an interview in under 10 minutes',
  community_connector: 'Make your first community connection',
};

const BadgeDetailModal = ({ open, onClose, badge }) => {
  if (!badge) return null;

  const {
    badge_key,
    name,
    description,
    icon_url,
    earned,
    earned_at,
    criteria,
    progress,
    progress_current,
    progress_target,
  } = badge;

  const emoji = BADGE_ICONS[badge_key] || '🏅';
  const earnedDate = earned_at
    ? new Date(earned_at).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const criteriaText = criteria || BADGE_CRITERIA[badge_key] || description;

  // Progress computation
  const hasProg = typeof progress_current === 'number' && typeof progress_target === 'number';
  const progressPct = hasProg
    ? Math.min(Math.round((progress_current / progress_target) * 100), 100)
    : typeof progress === 'number'
      ? Math.min(progress, 100)
      : null;

  const handleShare = () => {
    if (navigator.clipboard) {
      const text = `I earned the "${name}" badge on JobMockAI! 🏅`;
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="badge-detail-modal"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEvents color={earned ? 'warning' : 'disabled'} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Badge Details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close badge detail" data-testid="badge-modal-close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Badge icon */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          {icon_url ? (
            <Avatar
              src={icon_url}
              alt={name}
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 1,
                opacity: earned ? 1 : 0.4,
                filter: earned ? 'none' : 'grayscale(100%)',
              }}
            />
          ) : (
            <Typography
              variant="h2"
              sx={{
                mb: 1,
                opacity: earned ? 1 : 0.4,
                filter: earned ? 'none' : 'grayscale(100%)',
              }}
            >
              {emoji}
            </Typography>
          )}

          <Typography variant="h6" sx={{ fontWeight: 'bold' }} data-testid="badge-modal-name">
            {name}
          </Typography>

          <Chip
            icon={earned ? <CheckCircle /> : <Lock />}
            label={earned ? 'Earned' : 'Not Earned'}
            color={earned ? 'success' : 'default'}
            variant={earned ? 'filled' : 'outlined'}
            size="small"
            sx={{ mt: 1 }}
            data-testid="badge-modal-status"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="badge-modal-description">
          {description}
        </Typography>

        {/* Criteria */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            How to earn
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="badge-modal-criteria">
            {criteriaText}
          </Typography>
        </Box>

        {/* Earned date */}
        {earned && earnedDate && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Date earned
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="badge-modal-date">
              {earnedDate}
            </Typography>
          </Box>
        )}

        {/* Progress for unearned badges */}
        {!earned && progressPct !== null && (
          <Box sx={{ mt: 2 }} data-testid="badge-modal-progress">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {hasProg ? `${progress_current} / ${progress_target}` : `${progressPct}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{ height: 8, borderRadius: 4 }}
              color={progressPct >= 75 ? 'success' : progressPct >= 50 ? 'warning' : 'primary'}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {earned && (
          <Button
            startIcon={<Share />}
            size="small"
            onClick={handleShare}
            data-testid="badge-modal-share"
          >
            Copy to Share
          </Button>
        )}
        <Button onClick={onClose} variant="contained" size="small" data-testid="badge-modal-close-btn">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeDetailModal;
